import { defineComponent as _defineComponent } from 'vue'
import { vModelRadio as _vModelRadio, createElementVNode as _createElementVNode, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass, createVNode as _createVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"
import _imports_0 from '@/assets/icons/pile_60.png'
import _imports_1 from '@/assets/icons/grid_60.png'


const _withScopeId = n => (_pushScopeId("data-v-44209590"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "content-explorer" }
const _hoisted_2 = { class: "options-area" }
const _hoisted_3 = {
  key: 0,
  class: "btn-group",
  role: "group",
  "aria-label": "Layout"
}
const _hoisted_4 = ["value"]
const _hoisted_5 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("label", {
  class: "btn btn-outline-secondary btn-sm",
  for: "layoutPile"
}, [
  /*#__PURE__*/_createElementVNode("img", { src: _imports_0 })
], -1))
const _hoisted_6 = ["value"]
const _hoisted_7 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("label", {
  class: "btn btn-outline-secondary btn-sm",
  for: "layoutGrid"
}, [
  /*#__PURE__*/_createElementVNode("img", { src: _imports_1 })
], -1))
const _hoisted_8 = {
  key: 1,
  class: "btn-group",
  role: "group",
  "aria-label": "Filters"
}
const _hoisted_9 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("label", {
  class: "btn btn-outline-secondary btn-sm",
  for: "all"
}, "All", -1))
const _hoisted_10 = ["id", "value"]
const _hoisted_11 = ["for"]
const _hoisted_12 = { class: "content-bounds" }
const _hoisted_13 = ["onClick"]
const _hoisted_14 = ["src", "alt"]

import { computed, reactive, Ref, ref, watch } from 'vue'
import { Content, ItemType } from '@/rd/ResearchDataModel'
import { useViewPersonStore } from '@/rd/ViewPersonStore'
import { useViewFamilyStore } from '@/rd/ViewFamilyStore'
import { useContentTagStore } from '@/rd/ContentTagStore'
import { useContentStore } from '@/rd/ContentStore'
import { LoadMode } from '@/util/AsyncData'
import { hashCode, seededRandomFloat } from '@/util/RandomUtil'
import { useCommentStore } from '@/rd/CommentStore'
import ContentViewer from './ContentViewer.vue'

enum LayoutType {
  Pile = 'pile',
  Grid = 'grid',
}

interface PilePosition {
  x: number
  y: number
  z: number
  rotation: number
}

const MIN_DISTANCE = 25 // minimum distance between items in % of container

type Point = { x: number, y: number }

export default /*#__PURE__*/_defineComponent({
  __name: 'ContentExplorer',
  props: {
    personId: {}
  },
  setup(__props: any, { expose: __expose }) {

const props = __props;



__expose({
  getCount: () => contentItemsRef.value.length,
  hasSocialData: () => contentItemsRef.value.some(c => commentStore.getCommentCount(c.id!) > 0),
  showContent: onContentClick
})

const filterTypes = [
  { type: ItemType.Person, displayName: 'Person' },
  // { type: ItemType.Family, displayName: 'Family' },
  // { type: ItemType.Assertion, displayName: 'Details' },
  // { type: ItemType.Place, displayName: 'Places' },
]

const selectedLayoutRef = ref(LayoutType.Pile)
const selectedFilterRef = ref<ItemType>()
const contentItemsRef = ref([]) as Ref<Content[]>
const pileMap = reactive(new Map<string, PilePosition>())
const contentViewerRef = ref<InstanceType<typeof ContentViewer>>()

const viewPersonStore = useViewPersonStore()
const viewFamilyStore = useViewFamilyStore()
const contentTagStore = useContentTagStore()
const contentStore = useContentStore()
const commentStore = useCommentStore()

const viewPersonRef = computed(() => viewPersonStore.getLoadedPerson(props.personId))
const personContentTagsRef = computed(() => contentTagStore.getTagsForItems(ItemType.Person, viewPersonRef.value?.matchIds ?? [], LoadMode.EnsureLoaded))
const spouseFamiliesRef = computed(() => props.personId ? viewPersonStore.getLoadedSpouseFamilies(props.personId) : [])
//const familyContentTagsRef = computed(() => contentTagStore.getTagsForItems(ItemType.Family, [props.personId]))
const allContentTagsRef = computed(() => [...personContentTagsRef.value]) //, ...familyContentTagsRef.value])
const enablePileRef = computed(() => contentItemsRef.value.length > 4)
const layoutRef = computed(() => enablePileRef.value ? selectedLayoutRef.value : LayoutType.Grid)

const seedIndexRef = computed(() => hashCode(props.personId ?? '') % pileSeeds.length)

const pileSeeds = [
  // hand-picked values that produce a reasonably centered and distributed layout for 1-7 items
  1319026112,
  1319026114,
  1319026120,
  1319026126,
  1319026151,
]

watch([allContentTagsRef, seedIndexRef], async () => {
  const contentIds = new Set(allContentTagsRef.value.map(t => t.contentId!))
  const contentItems = await contentStore.getContentsAsync([...contentIds])

  // HACK
  const seed = pileSeeds[seedIndexRef.value]
  
  const xValues = seededRandomFloat(seed)
  const yValues = seededRandomFloat(seed + 1)
  const rotationValues = seededRandomFloat(seed + 2)

  pileMap.clear()
  const positions = [] as Point[]

  for (let i = 0; i < contentItems.length; i++) {
    const c = contentItems[i]
    if (!pileMap.has(c.id!)) {
      let x, y, attempts = 0
      do {
        x = xValues.next().value * 100 // %
        y = yValues.next().value * 100 // %
      } while (attempts++ < 10 && hasCollision(x, y, positions))
      if (attempts >= 20) {
        console.warn(`ContentExplorer: failed to place content without collision`)
      }
      positions.push({ x, y })

      const z = contentItems.length - i // first content item on top
      const rotation = rotationValues.next().value * 60 - 30 // +/- 30 degrees

      pileMap.set(c.id!, { x, y, z, rotation })
    }
  }

  contentItemsRef.value = contentItems
  commentStore.ensureSeeded(contentItems.map(c => c.id!))
})

function hasCollision(x: number, y: number, positions: Point[]) {
  for (const p of positions) {
    const dx = p.x - x
    const dy = p.y - y
    const distance = Math.sqrt(dx * dx + dy * dy)
    if (distance < MIN_DISTANCE) {
      return true // too close
    }
  }
  return false
}

function getContentStyle(c: Content) {
  if (layoutRef.value != LayoutType.Pile)
    return {}

  const position = pileMap.get(c.id!)!
  return {
    left: position.x + '%',
    top: position.y + '%',
    zIndex: position.z,
    transform: `translate(-50%, -50%) rotate(${position.rotation}deg)`,
  }
}

function onContentClick(contentId: string) {
  const index = contentItemsRef.value.findIndex(item => item.id === contentId)
  contentViewerRef.value?.open(contentItemsRef.value, index)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      (enablePileRef.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _withDirectives(_createElementVNode("input", {
              type: "radio",
              class: "btn-check",
              name: "layout",
              id: "layoutPile",
              autocomplete: "off",
              value: LayoutType.Pile,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedLayoutRef).value = $event))
            }, null, 8, _hoisted_4), [
              [_vModelRadio, selectedLayoutRef.value]
            ]),
            _hoisted_5,
            _withDirectives(_createElementVNode("input", {
              type: "radio",
              class: "btn-check",
              name: "layout",
              id: "layoutGrid",
              autocomplete: "off",
              value: LayoutType.Grid,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedLayoutRef).value = $event))
            }, null, 8, _hoisted_6), [
              [_vModelRadio, selectedLayoutRef.value]
            ]),
            _hoisted_7
          ]))
        : _createCommentVNode("", true),
      false
        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
            _withDirectives(_createElementVNode("input", {
              type: "radio",
              class: "btn-check",
              name: "filters",
              id: "all",
              autocomplete: "off",
              value: undefined,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((selectedFilterRef).value = $event))
            }, null, 512), [
              [_vModelRadio, selectedFilterRef.value]
            ]),
            _hoisted_9,
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(filterTypes, (ft) => {
              return (_openBlock(), _createElementBlock(_Fragment, {
                key: ft.type
              }, [
                _withDirectives(_createElementVNode("input", {
                  type: "radio",
                  class: "btn-check",
                  name: "filters",
                  id: `filter${ft.type}`,
                  autocomplete: "off",
                  value: ft.type,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((selectedFilterRef).value = $event))
                }, null, 8, _hoisted_10), [
                  [_vModelRadio, selectedFilterRef.value]
                ]),
                _createElementVNode("label", {
                  class: "btn btn-outline-secondary btn-sm",
                  for: `filter${ft.type}`
                }, _toDisplayString(ft.displayName), 9, _hoisted_11)
              ], 64))
            }), 64))
          ]))
        : _createCommentVNode("", true)
    ]),
    (contentItemsRef.value.length)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: _normalizeClass(["content-area", layoutRef.value.toLowerCase()])
        }, [
          _createElementVNode("div", _hoisted_12, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(contentItemsRef.value, (c) => {
              return (_openBlock(), _createElementBlock("button", {
                type: "button",
                key: c.id,
                class: "btn btn-unstyled",
                onClick: ($event: any) => (onContentClick(c.id!))
              }, [
                _createElementVNode("img", {
                  src: c.galleryUrl,
                  alt: c.caption,
                  style: _normalizeStyle(getContentStyle(c))
                }, null, 12, _hoisted_14)
              ], 8, _hoisted_13))
            }), 128))
          ])
        ], 2))
      : _createCommentVNode("", true),
    _createVNode(ContentViewer, {
      ref_key: "contentViewerRef",
      ref: contentViewerRef
    }, null, 512)
  ]))
}
}

})